import { MemberType } from '@lucidlink/dto/validation';
import { TokensResponseDto, } from '@lucidlink/dto/web-service-api';
import { AES_ENCRYPTION_ALGO_PARAMS, AES_KW_ENCRYPTION_ALGO_PARAMS, arrayToAesKey, base64ToArray, deriveAesSecretKey, derivePasswordKey, stringToCryptoKey, } from '@lucidlink/ts-crypto/crypto';
import { isNil } from '@lucidlink/tscore';
import { createMemKvStorage } from '#kv-storage';
import { FilespaceTokenManager } from './filespace-token-manager';
import { SessionContext } from './session-context';
import { InvalidatedTokenError, JwtTokenManager, ServiceAccountTokenManager, } from './token-manager';
import { TokenPair } from './token-pair';
export const GLOBAL_TOKEN_ENDPOINT = 'user-login';
export const WORKSPACE_TOKEN_ENDPOINT = 'workspace-login';
export const WORKSPACES_ENDPOINT = 'workspaces';
export const FILESPACE_TOKEN_ENDPOINT = 'filespace-login';
export class SessionManager {
    kvStorage;
    getUnauthenticatedFetcher;
    onGlobalLogin;
    onGlobalLogout;
    onWorkspaceLogin;
    onWorkspaceLogout;
    globalTokenManager;
    workspaceTokenManagers = new Map();
    filespaceTokenManagers = new FilespaceTokenManager();
    sessionContext;
    static create({ kvStorage, getUnauthenticatedFetcher, onGlobalLogin, onGlobalLogout, onWorkspaceLogin, onWorkspaceLogout, }) {
        return new SessionManager(kvStorage, getUnauthenticatedFetcher, onGlobalLogin, onGlobalLogout, onWorkspaceLogin, onWorkspaceLogout);
    }
    constructor(kvStorage, getUnauthenticatedFetcher, onGlobalLogin, onGlobalLogout, onWorkspaceLogin, onWorkspaceLogout) {
        this.kvStorage = kvStorage;
        this.getUnauthenticatedFetcher = getUnauthenticatedFetcher;
        this.onGlobalLogin = onGlobalLogin;
        this.onGlobalLogout = onGlobalLogout;
        this.onWorkspaceLogin = onWorkspaceLogin;
        this.onWorkspaceLogout = onWorkspaceLogout;
        this.sessionContext = new SessionContext(this.kvStorage);
    }
    async logout() {
        if (this.globalTokenManager) {
            await this.globalTokenManager.invalidateToken();
        }
        this.globalTokenManager = undefined;
        this.workspaceTokenManagers.clear();
        this.filespaceTokenManagers = new FilespaceTokenManager();
        await this.kvStorage.clear();
        this.onGlobalLogout();
    }
    async globalLoginByPassword(userLoginResponse, userLoginParams) {
        const { userEncryptedKey, userEncryptedKeyIterations, userEncryptedKeySalt, userEncryptedKeyGen, } = userLoginResponse;
        const { email, password: baseKey } = userLoginParams;
        return this.globalLogin({ ...userLoginResponse, email }, {
            userEncryptedKey,
            userEncryptedKeyGen,
            baseKey,
        }, () => derivePasswordKey(baseKey, userEncryptedKeyIterations, userEncryptedKeySalt));
    }
    async loginByServiceAccount(serviceAccount, token) {
        if (this.globalTokenManager) {
            throw new AlreadyLoggedInError();
        }
        const tokenManager = await ServiceAccountTokenManager.create(token, this.kvStorage);
        this.globalTokenManager = tokenManager;
        const { id, name, serviceIdentity: { eAccountKeyByServiceIdentitySecretKey }, workspaceId, } = serviceAccount;
        await this.sessionContext.storeAccountDetails({
            serviceAccountId: id,
            name,
            type: MemberType.SERVICE_ACCOUNT,
            workspaceId,
            wrappingKey: await stringToCryptoKey(token.secretKey, {
                ...AES_KW_ENCRYPTION_ALGO_PARAMS,
                keyUsages: ['unwrapKey'],
            }),
            eAccountKey: eAccountKeyByServiceIdentitySecretKey,
            accountKeyGen: 1,
        });
        await this.onGlobalLogin();
        this.workspaceTokenManagers.set(workspaceId, tokenManager);
        this.onWorkspaceLogin(workspaceId);
    }
    async globalLoginBySsoSecret(ssoUserLoginResponse, ssoUserLoginParams) {
        const { userEncryptedKey, userEncryptedKeySalt, email } = ssoUserLoginResponse;
        const { domainSecret: baseKey } = ssoUserLoginParams;
        await this.globalLogin({ ...ssoUserLoginResponse, email }, {
            userEncryptedKey,
            baseKey,
        }, () => deriveAesSecretKey(baseKey, userEncryptedKeySalt));
        await this.storeDomainKey(baseKey);
    }
    async tryRestoreDomainKey() {
        return this.kvStorage.get(domainKey());
    }
    async storeDomainKey(key) {
        const cryptoKey = await arrayToAesKey(base64ToArray(key), {
            ...AES_ENCRYPTION_ALGO_PARAMS,
            extractable: false,
        });
        await this.kvStorage.put(domainKey(), cryptoKey);
    }
    async globalLoginByVerificationCode(userParams) {
        return this.globalLogin(userParams);
    }
    async tryRestoreLogin() {
        if (this.globalTokenManager) {
            return true;
        }
        const account = await this.sessionContext.getAccountDetails();
        if (isNil(account)) {
            await this.logout();
            return false;
        }
        const tokenManager = account.type === MemberType.USER
            ? await JwtTokenManager.tryCreateFromStorage({
                tokenName: globalTokenKey(),
                getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
                tokenStorage: this.kvStorage,
                tokenEndpoint: GLOBAL_TOKEN_ENDPOINT,
            })
            : await ServiceAccountTokenManager.tryCreateFromStorage(this.kvStorage);
        if (isNil(tokenManager)) {
            await this.logout();
            return false;
        }
        this.globalTokenManager = tokenManager;
        await this.onGlobalLogin();
        if (account.type === MemberType.SERVICE_ACCOUNT) {
            this.workspaceTokenManagers.set(account.workspaceId, tokenManager);
            this.onWorkspaceLogin(account.workspaceId);
        }
        return true;
    }
    async getFreshGlobalToken(forceRefresh) {
        if (!this.globalTokenManager) {
            throw new NotLoggedInError();
        }
        try {
            return await this.globalTokenManager.getFreshToken(forceRefresh);
        }
        catch (error) {
            if (error instanceof InvalidatedTokenError) {
                await this.logout();
                throw new NotLoggedInError();
            }
            throw error;
        }
    }
    async getAccountDetails() {
        const account = await this.sessionContext.getAccountDetails();
        if (isNil(account)) {
            throw new NotLoggedInError();
        }
        return account;
    }
    // Workspace
    async workspaceLogin(workspaceId, entityId, workspaceLoginResponse) {
        const tokenManager = await JwtTokenManager.createWithTokens({
            tokenName: workspaceTokenKey(entityId),
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
            tokenStorage: this.kvStorage,
            tokenEndpoint: WORKSPACE_TOKEN_ENDPOINT,
            tokens: TokenPair.fromResponseDto(workspaceLoginResponse.tokens),
        });
        this.workspaceTokenManagers.set(entityId, tokenManager);
        this.onWorkspaceLogin(workspaceId);
    }
    async tryRestoreWorkspaceLogin(workspaceId, entityId) {
        const guestSecretKey = await this.getGuestSecretFromStorage(entityId);
        if (this.isLoggedInToWorkspace(entityId)) {
            return {
                isLoggedIn: true,
                guestSecretKey: !isNil(guestSecretKey)
                    ? guestSecretKey
                    : undefined,
            };
        }
        const tokenManager = await JwtTokenManager.tryCreateFromStorage({
            tokenName: workspaceTokenKey(entityId),
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
            tokenStorage: this.kvStorage,
            tokenEndpoint: WORKSPACE_TOKEN_ENDPOINT,
        });
        if (isNil(tokenManager)) {
            return { isLoggedIn: false };
        }
        this.workspaceTokenManagers.set(entityId, tokenManager);
        this.onWorkspaceLogin(workspaceId);
        return {
            isLoggedIn: true,
            guestSecretKey: !isNil(guestSecretKey) ? guestSecretKey : undefined,
        };
    }
    async workspaceLogout(workspaceId, entityId) {
        const workspaceTokenManager = this.workspaceTokenManagers.get(entityId);
        if (workspaceTokenManager) {
            await workspaceTokenManager.invalidateToken();
            this.workspaceTokenManagers.delete(entityId);
        }
        const entityIds = this.filespaceTokenManagers.listFilespacesForWorkspace(entityId);
        for (const entityId of entityIds) {
            await this.filespaceLogout(entityId);
        }
        await this.kvStorage.remove(workspaceTokenKey(entityId));
        await this.kvStorage.remove(guestSecretKey(entityId));
        this.onWorkspaceLogout(workspaceId);
    }
    async getFreshWorkspaceToken(workspaceId, entityId, forceRefresh) {
        const workspaceTokenManager = this.workspaceTokenManagers.get(entityId);
        if (!workspaceTokenManager) {
            throw getNotLoggedInError({ workspaceId, entityId });
        }
        try {
            return await workspaceTokenManager.getFreshToken(forceRefresh);
        }
        catch (error) {
            if (error instanceof InvalidatedTokenError) {
                await this.workspaceLogout(workspaceId, entityId);
                throw getNotLoggedInError({ workspaceId, entityId });
            }
            throw error;
        }
    }
    async storeGuestSecret(entityId, key) {
        await this.kvStorage.put(guestSecretKey(entityId), key);
    }
    async getGuestSecretFromStorage(entityId) {
        return this.kvStorage.get(guestSecretKey(entityId));
    }
    // Filespace
    async filespaceLogin(filespace, filespaceTokens, entityId) {
        if (!this.isLoggedInToWorkspace(entityId ?? filespace.workspaceId)) {
            throw getNotLoggedInError({
                workspaceId: filespace.workspaceId,
                entityId,
            });
        }
        if (this.isLoggedInToFilespace(entityId ?? filespace.id)) {
            return;
        }
        const tokenManager = await JwtTokenManager.createWithTokens({
            tokenName: filespaceTokenKey(filespace.id),
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
            tokenStorage: await createMemKvStorage(),
            tokenEndpoint: `${WORKSPACES_ENDPOINT}/${filespace.workspaceId}/${FILESPACE_TOKEN_ENDPOINT}`,
            tokens: TokenPair.fromResponseDto(filespaceTokens),
        });
        this.filespaceTokenManagers.setTokenManager(entityId ?? filespace.workspaceId, entityId ?? filespace.id, tokenManager);
    }
    async filespaceLogout(entityId) {
        const filespaceTokenManager = this.filespaceTokenManagers.getTokenManager(entityId);
        if (filespaceTokenManager) {
            await filespaceTokenManager.invalidateToken();
            this.filespaceTokenManagers.removeTokenManager(entityId);
        }
        await this.kvStorage.remove(filespaceTokenKey(entityId));
    }
    async getFreshFilespaceToken(workspaceId, filespaceId, forceRefresh, entityId) {
        const filespaceTokenManager = this.filespaceTokenManagers.getTokenManager(entityId ?? filespaceId);
        if (!filespaceTokenManager) {
            throw getNotLoggedInError({ workspaceId, filespaceId, entityId });
        }
        try {
            return await filespaceTokenManager.getFreshToken(forceRefresh);
        }
        catch (error) {
            if (error instanceof InvalidatedTokenError) {
                await this.filespaceLogout(entityId ?? filespaceId);
                throw getNotLoggedInError({
                    workspaceId,
                    filespaceId,
                    entityId,
                });
            }
            throw error;
        }
    }
    isLoggedInToWorkspace(entityId) {
        return !isNil(this.workspaceTokenManagers.get(entityId));
    }
    isLoggedInToFilespace(filespaceId) {
        return !isNil(this.filespaceTokenManagers.getTokenManager(filespaceId));
    }
    async globalLogin(userParams, cryptoParams, deriveKey) {
        if (this.globalTokenManager) {
            throw new AlreadyLoggedInError();
        }
        this.globalTokenManager = await JwtTokenManager.createWithTokens({
            tokenName: globalTokenKey(),
            tokens: TokenPair.fromResponseDto(TokensResponseDto.create(userParams)),
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
            tokenStorage: this.kvStorage,
            tokenEndpoint: GLOBAL_TOKEN_ENDPOINT,
        });
        const accountDetails = {
            userId: userParams.userId,
            email: userParams.email,
            type: MemberType.USER,
        };
        if (cryptoParams && deriveKey) {
            const derivedKey = await deriveKey();
            await this.sessionContext.storeAccountDetails({
                ...accountDetails,
                wrappingKey: derivedKey,
                eAccountKey: cryptoParams.userEncryptedKey,
                accountKeyGen: cryptoParams.userEncryptedKeyGen,
            });
        }
        else {
            await this.sessionContext.storeAccountDetails(accountDetails);
        }
        await this.onGlobalLogin();
    }
}
const globalTokenKey = () => '$GlobalToken';
const workspaceTokenKey = (entityId) => `$WorkspaceToken.${entityId}`;
const filespaceTokenKey = (filespaceId) => `$FilespaceToken.${filespaceId}`;
const guestSecretKey = (entityId) => `$GuestSecret.${entityId}`;
const domainKey = () => '$DomainKey';
export class AlreadyLoggedInError extends Error {
    constructor() {
        super('Already logged in');
    }
}
export class NotLoggedInError extends Error {
    constructor() {
        super('Not logged in');
    }
}
export class NotLoggedInToWorkspaceError extends Error {
    workspaceId;
    constructor(workspaceId) {
        super(`Not logged in to workspace ${workspaceId}`);
        this.workspaceId = workspaceId;
    }
}
export class NotLoggedInToFilespaceError extends Error {
    constructor(filespaceId) {
        super(`Not logged in to filespace ${filespaceId}`);
    }
}
export class NotLoggedInWithGuestLinkError extends Error {
    linkId;
    workspaceId;
    constructor(linkId, workspaceId) {
        super(`Not logged in with guest link ${linkId}`);
        this.linkId = linkId;
        this.workspaceId = workspaceId;
    }
}
export function getNotLoggedInError(ids) {
    switch (true) {
        case ids.filespaceId === undefined && ids.entityId === undefined:
            return new NotLoggedInToWorkspaceError(ids.workspaceId);
        case ids.filespaceId !== undefined: {
            if (ids.entityId === undefined) {
                return new NotLoggedInToFilespaceError(ids.filespaceId);
            }
            return new NotLoggedInWithGuestLinkError(ids.entityId, ids.workspaceId);
        }
        case ids.entityId !== undefined: {
            if (ids.workspaceId === ids.entityId) {
                return new NotLoggedInToWorkspaceError(ids.workspaceId);
            }
            return new NotLoggedInWithGuestLinkError(ids.entityId, ids.workspaceId);
        }
        default:
            throw new Error('UNREACHABLE');
    }
}
